import { useForm, revalidateLogic } from "@tanstack/react-form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"

import {
  ScheduleFormFields,
  createScheduleFormState,
  validateScheduleInput,
  type ScheduleFormState,
} from "@/components/guild-events-admin/schedule-form-fields"
import { StatusAlert } from "@/components/shared/status-alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/toast"
import {
  guildEventsApi,
  type GuildEventSchedule,
  type GuildEventScheduleCreateInput,
} from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"

export type ScheduleDefinitionFormValues = ScheduleFormState & {
  channelId: string
}
const createScheduleDefinitionFormValues =
  (): ScheduleDefinitionFormValues => ({
    ...createScheduleFormState(),
    channelId: "",
  })

export function ScheduleDefinitionForm({
  scheduleId,
}: {
  scheduleId?: string
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const schedulesQuery = useQuery({
    queryKey: guildEventsKeys.schedules(),
    queryFn: guildEventsApi.listSchedules,
    enabled: scheduleId !== undefined,
  })
  const [formError, setFormError] = useState<string | null>(null)
  const schedule = schedulesQuery.data?.find((item) => item.id === scheduleId)
  const createMutation = useMutation({
    mutationFn: guildEventsApi.createSchedule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.schedules(),
      })
      toast.success("Schedule created")
      await navigate({ to: "/guild-events/schedules" })
    },
    onError: (error: Error) => toast.error(error.message),
  })
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string
      input: GuildEventScheduleCreateInput
    }) => guildEventsApi.updateSchedule(id, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.schedules(),
      })
      toast.success("Schedule updated")
      await navigate({ to: "/guild-events/schedules" })
    },
    onError: (error: Error) => toast.error(error.message),
  })
  const form = useForm({
    defaultValues: createScheduleDefinitionFormValues(),
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: ({ value }) => validateScheduleInput(value) },
    onSubmitInvalid: () => {
      setFormError("Fix the highlighted errors.")
      for (const field of [
        "title",
        "weekdays",
        "startTime",
        "eventRoles",
        "signupCloseTime",
      ] as const) {
        form.setFieldMeta(field, (meta) => ({ ...meta, isTouched: true }))
      }
    },
    onSubmit: ({ value }) => {
      setFormError(null)
      const input = projectScheduleInput(value)
      if (schedule) updateMutation.mutate({ id: schedule.id, input })
      else createMutation.mutate(input)
    },
  })
  const routeIdRef = useRef<string | undefined>(undefined)
  const hydratedRouteIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (routeIdRef.current !== scheduleId) {
      routeIdRef.current = scheduleId
      hydratedRouteIdRef.current = undefined
      form.reset(createScheduleDefinitionFormValues())
    }
    if (schedule && hydratedRouteIdRef.current !== scheduleId) {
      form.reset(toScheduleDefinitionFormValues(schedule))
      hydratedRouteIdRef.current = scheduleId
    }
  }, [form, schedule, scheduleId])

  if (scheduleId && schedulesQuery.isLoading)
    return <p className="text-sm text-muted-foreground">Loading schedule...</p>
  if (scheduleId && schedulesQuery.isError)
    return (
      <StatusAlert tone="danger">
        <div className="flex items-center justify-between gap-3">
          <span>Unable to load this schedule.</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void schedulesQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      </StatusAlert>
    )
  if (scheduleId && !schedule)
    return (
      <StatusAlert tone="danger">This schedule no longer exists.</StatusAlert>
    )

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isEditing = schedule !== undefined
  return (
    <Card>
      <CardHeader className="gap-1">
        <CardTitle>
          {schedule ? `Edit ${schedule.title}` : "New schedule"}
        </CardTitle>
        <CardDescription>
          Set the event template, recurrence, and Discord destination in one
          place.
        </CardDescription>
      </CardHeader>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void form.handleSubmit()
        }}
      >
        <CardContent className="grid gap-5">
          <form.Subscribe selector={(state) => state.values}>
            {(values) => (
              <ScheduleFormFields
                bindings={{
                  values,
                  eventKind: (children) => (
                    <form.Field name="eventKind">{children}</form.Field>
                  ),
                  title: (children) => (
                    <form.Field name="title">{children}</form.Field>
                  ),
                  description: (children) => (
                    <form.Field name="description">{children}</form.Field>
                  ),
                  eventRoles: (children) => (
                    <form.Field name="eventRoles">{children}</form.Field>
                  ),
                  weekdays: (children) => (
                    <form.Field name="weekdays">{children}</form.Field>
                  ),
                  startTime: (children) => (
                    <form.Field name="startTime">{children}</form.Field>
                  ),
                  signupCloseTime: (children) => (
                    <form.Field name="signupCloseTime">{children}</form.Field>
                  ),
                  rsvpEnabled: (children) => (
                    <form.Field name="rsvpEnabled">{children}</form.Field>
                  ),
                  setEventRoles: (eventRoles) =>
                    form.setFieldValue("eventRoles", eventRoles),
                  setSignupCloseTime: (signupCloseTime) =>
                    form.setFieldValue(
                      "signupCloseTime",
                      signupCloseTime ?? "",
                    ),
                }}
              />
            )}
          </form.Subscribe>
          <form.Field name="channelId">
            {(field) => (
              <section
                aria-labelledby="schedule-delivery-heading"
                className="grid gap-3 border-y border-border/60 py-4"
              >
                <div className="grid gap-1">
                  <h3 id="schedule-delivery-heading" className="font-semibold">
                    Delivery
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Leave the channel blank to use the configured Guild Events
                    channel.
                  </p>
                </div>
                <div className="grid max-w-xl gap-2">
                  <Label htmlFor="schedule-channel-id">
                    Discord channel ID (optional)
                  </Label>
                  <Input
                    id="schedule-channel-id"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    placeholder="Uses the configured Guild Events channel when blank"
                  />
                </div>
              </section>
            )}
          </form.Field>
          {formError ? (
            <StatusAlert tone="danger">{formError}</StatusAlert>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: "/guild-events/schedules" })}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving
              ? "Saving..."
              : isEditing
                ? "Save schedule"
                : "Create schedule"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}

export function projectScheduleInput(
  value: ScheduleDefinitionFormValues,
): GuildEventScheduleCreateInput {
  const common = {
    title: value.title.trim(),
    description: value.description.trim(),
    channelId: value.channelId.trim() || undefined,
    weekdays: value.weekdays,
    startTime: value.startTime,
    rsvpEnabled: value.rsvpEnabled,
  }
  if (value.eventKind === "attendance")
    return {
      ...common,
      eventKind: "attendance",
      eventRoles: [],
      signupCloseTime: null,
    }
  return {
    ...common,
    eventKind: "raid",
    eventRoles: value.eventRoles,
    signupCloseTime: value.signupCloseTime,
  }
}

export function toScheduleDefinitionFormValues(
  schedule: GuildEventSchedule,
): ScheduleDefinitionFormValues {
  const common = {
    title: schedule.title,
    description: schedule.description,
    channelId: schedule.channelId,
    weekdays: schedule.weekdays,
    startTime: schedule.startTime,
    rsvpEnabled: schedule.rsvpEnabled,
  }
  return schedule.eventKind === "raid"
    ? {
        ...common,
        eventKind: "raid",
        eventRoles: schedule.eventRoles,
        signupCloseTime: schedule.signupCloseTime ?? "",
      }
    : {
        ...common,
        eventKind: "attendance",
        eventRoles: [],
        signupCloseTime: null,
      }
}
